// Identity of a kiosk variant. (TKT-2026-105367)
//
// The kiosk used to treat `skuId` as the identity of a sellable copy, because the
// server grouped stock rows by productId + skuId and so could only ever serve one
// option per SKU. That was wrong: a catalog skuId covers several physically
// different things. A graded slab is stored against the raw card's skuId with its
// own condAbbr, so one skuId could mean a Grade 10 slab AND five raw NM copies.
//
// The server now groups on the same tuple checkout reserves on (skuId, condAbbr,
// printing, langAbbr), which means a single skuId can appear more than once in a
// product's variant list. Every place that used to compare or key on `skuId`
// alone has to use this instead, or two different cards collapse back into one
// cart line and the shopper is charged for the copy they did not pick.
//
// The key is a display-independent string so it can be a React key, a Select
// value, and a Map key without further conversion.

/**
 * Stable identity for one kiosk variant.
 *
 * @param {Object} variant a variant as served by /products/:store/... or held on
 *        a cart line (`item.variant`)
 * @returns {string} identity, stable across refetches of the same copy
 */
export function variantKey(variant) {
  if (!variant) return '';
  const part = (value, fallback) => {
    const s = value === null || value === undefined ? '' : String(value).trim();
    return s || fallback;
  };
  const key = [
    part(variant.skuId, ''),
    part(variant.condAbbr, 'NM'),
    part(variant.printing, 'Normal'),
    part(variant.langAbbr, 'EN'),
  ].join('|');
  // A Sell To Us slab has no SKU, and its grade is its condition, so a PSA 10
  // and a BGS 10 of one card would share every part above. The grader and the
  // cert are what tell them apart. A variant with no grader keys exactly as
  // before. TKT-2026-107322
  const grader = part(variant.gradingCompany, '').toUpperCase();
  if (!grader) return key;
  return `${key}|${grader}|${part(variant.certNumber, '')}`;
}

/** True when two variants are the same physical offering. */
export function sameVariant(a, b) {
  const ka = variantKey(a);
  return Boolean(ka) && ka === variantKey(b);
}

export default variantKey;
